import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import type { JournalTemplate, CreateJournalEntryRequest } from '@/types/journal-api';
import { Loader2, Save, X } from 'lucide-react';

interface Props {
    template: JournalTemplate;
    date: string;
    initialData?: Record<string, any>;
    onSubmit: (data: CreateJournalEntryRequest) => void;
    onCancel?: () => void;
    isSubmitting?: boolean;
}

export const DynamicJournalForm: React.FC<Props> = ({
    template,
    date,
    initialData = {},
    onSubmit,
    onCancel,
    isSubmitting = false,
}) => {
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: initialData,
    });

    const [responses, setResponses] = useState<Record<string, any>>(initialData);

    const updateResponse = (fieldName: string, value: any) => {
        setResponses((prev) => ({ ...prev, [fieldName]: value }));
        setValue(fieldName, value);
    };

    const renderField = (field: any) => {
        const {
            name,
            label,
            type,
            required,
            placeholder,
            helpText,
            options,
            min,
            max,
            defaultValue,
        } = field;

        const fieldProps = {
            id: name,
            ...register(name, {
                required: required ? `${label} is required` : false,
                ...(type === 'NUMBER' && {
                    valueAsNumber: true,
                    min: { value: min || 0, message: `Minimum value is ${min || 0}` },
                }),
                ...(type === 'SCALE' && {
                    valueAsNumber: true,
                    min: { value: min, message: `Minimum value is ${min}` },
                    max: { value: max, message: `Maximum value is ${max}` },
                }),
            }),
        };

        switch (type) {
            case 'TEXT':
                return (
                    <div key={name} className="space-y-2">
                        <Label htmlFor={name} className="text-sm font-medium">
                            {label} {required && <span className="text-destructive">*</span>}
                        </Label>
                        <Textarea
                            {...fieldProps}
                            placeholder={placeholder}
                            className="min-h-[100px] resize-none"
                        />
                        {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
                        {errors[name] && (
                            <p className="text-xs text-destructive">
                                {errors[name]?.message as string}
                            </p>
                        )}
                    </div>
                );

            case 'NUMBER':
                return (
                    <div key={name} className="space-y-2">
                        <Label htmlFor={name} className="text-sm font-medium">
                            {label} {required && <span className="text-destructive">*</span>}
                        </Label>
                        <Input
                            {...fieldProps}
                            type="number"
                            placeholder={placeholder}
                            min={min}
                            max={max}
                        />
                        {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
                        {errors[name] && (
                            <p className="text-xs text-destructive">
                                {errors[name]?.message as string}
                            </p>
                        )}
                    </div>
                );

            case 'SCALE':
                return (
                    <div key={name} className="space-y-2">
                        <Label htmlFor={name} className="text-sm font-medium">
                            {label} {required && <span className="text-destructive">*</span>}
                        </Label>
                        <div className="flex items-center space-x-4">
                            <span className="text-sm text-muted-foreground">{min}</span>
                            <Input
                                {...fieldProps}
                                type="range"
                                min={min}
                                max={max}
                                className="flex-1"
                                onChange={(e) => updateResponse(name, Number(e.target.value))}
                            />
                            <span className="text-sm text-muted-foreground">{max}</span>
                            <span className="text-sm font-medium w-8">
                                {responses[name] || defaultValue || min}
                            </span>
                        </div>
                        {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
                        {errors[name] && (
                            <p className="text-xs text-destructive">
                                {errors[name]?.message as string}
                            </p>
                        )}
                    </div>
                );

            case 'MULTIPLE_CHOICE':
                return (
                    <div key={name} className="space-y-2">
                        <Label htmlFor={name} className="text-sm font-medium">
                            {label} {required && <span className="text-destructive">*</span>}
                        </Label>
                        <Select
                            value={responses[name] || defaultValue || ''}
                            onValueChange={(value) => updateResponse(name, value)}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder={placeholder || 'Select an option'} />
                            </SelectTrigger>
                            <SelectContent>
                                {options?.map((option: string) => (
                                    <SelectItem key={option} value={option}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
                        {errors[name] && (
                            <p className="text-xs text-destructive">
                                {errors[name]?.message as string}
                            </p>
                        )}
                    </div>
                );

            case 'BOOLEAN':
                return (
                    <div key={name} className="space-y-2">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id={name}
                                checked={responses[name] || defaultValue || false}
                                onCheckedChange={(checked) => updateResponse(name, checked)}
                            />
                            <Label htmlFor={name} className="text-sm font-medium">
                                {label} {required && <span className="text-destructive">*</span>}
                            </Label>
                        </div>
                        {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
                        {errors[name] && (
                            <p className="text-xs text-destructive">
                                {errors[name]?.message as string}
                            </p>
                        )}
                    </div>
                );

            case 'DATE':
                return (
                    <div key={name} className="space-y-2">
                        <Label htmlFor={name} className="text-sm font-medium">
                            {label} {required && <span className="text-destructive">*</span>}
                        </Label>
                        <Input {...fieldProps} type="date" />
                        {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
                        {errors[name] && (
                            <p className="text-xs text-destructive">
                                {errors[name]?.message as string}
                            </p>
                        )}
                    </div>
                );

            case 'TIME':
                return (
                    <div key={name} className="space-y-2">
                        <Label htmlFor={name} className="text-sm font-medium">
                            {label} {required && <span className="text-destructive">*</span>}
                        </Label>
                        <Input {...fieldProps} type="time" />
                        {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
                        {errors[name] && (
                            <p className="text-xs text-destructive">
                                {errors[name]?.message as string}
                            </p>
                        )}
                    </div>
                );

            default:
                return (
                    <div key={name} className="space-y-2">
                        <Label htmlFor={name} className="text-sm font-medium">
                            {label} {required && <span className="text-destructive">*</span>}
                        </Label>
                        <Input {...fieldProps} placeholder={placeholder} />
                        {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
                        {errors[name] && (
                            <p className="text-xs text-destructive">
                                {errors[name]?.message as string}
                            </p>
                        )}
                    </div>
                );
        }
    };

    const onFormSubmit = (formData: any) => {
        onSubmit({
            templateId: template.id,
            date,
            courseId: template.courseId,
            responses: { ...responses, ...formData },
            isCompleted: true,
        });
    };

    return (
        <Card className="p-6">
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-foreground">{template.name}</h2>
                {template.description && (
                    <p className="text-sm text-muted-foreground mt-2">{template.description}</p>
                )}
                {template.course && (
                    <div className="mt-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-brand-fill-lime text-brand-fill-ink">
                        {template.course.name}
                    </div>
                )}
            </div>

            <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
                {template.fields.map((field) => renderField(field))}

                <div className="flex justify-end space-x-3 pt-6 border-t">
                    {onCancel && (
                        <Button type="button" variant="outline" onClick={onCancel}>
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                        </Button>
                    )}
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        Save Entry
                    </Button>
                </div>
            </form>
        </Card>
    );
};
